import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, PageHeader, Pill, dt } from "./_shared";
import { Send, Eye, Repeat } from "lucide-react";

export default function AdminCommunications() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState("history"); // history | preview | send-test
  const [previewName, setPreviewName] = useState("Dr. João da Silva");
  const [previewTreatment, setPreviewTreatment] = useState("Dr.");
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewing, setPreviewing] = useState(false);

  const [testEmail, setTestEmail] = useState("");
  const [testName, setTestName] = useState("");
  const [testTreatment, setTestTreatment] = useState("Dr.");
  const [testResult, setTestResult] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/communications?kind=welcome&page_size=50").then(r => {
    setRows(r.data.rows); setTotal(r.data.total);
  });
  useEffect(() => { load(); }, []);

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const p = new URLSearchParams({ name: previewName, treatment: previewTreatment });
      const r = await fetch(`${API}/admin/communications/welcome/preview?${p}`, { credentials: "include" });
      const blob = await r.blob();
      setPreviewSrc(URL.createObjectURL(blob));
    } finally { setPreviewing(false); }
  };

  const sendTest = async () => {
    setSending(true); setTestResult(null);
    try {
      const r = await api.post("/admin/communications/welcome/test", {
        to: testEmail || undefined,
        name: testName || undefined,
        treatment: testTreatment,
      });
      setTestResult(r.data);
      await load();
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || String(e) });
    } finally { setSending(false); }
  };

  const reissue = async (userId, name) => {
    if (!confirm(`Reenviar cartão de boas-vindas para ${name}?`)) return;
    const r = await api.post(`/admin/communications/welcome/reissue/${userId}`);
    alert(`Status: ${r.data.status}`);
    await load();
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-communications">
      <PageHeader
        title="Comunicações"
        subtitle={`${total} envio(s) de cartão de boas-vindas registrado(s)`}
        right={
          <div className="flex bg-[#0f1524] border border-white/5 rounded-xl p-1" data-testid="admin-comm-tabs">
            {[
              { k: "history", l: "Histórico" },
              { k: "preview", l: "Prévia" },
              { k: "send-test", l: "Envio de teste" },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`px-3 py-1.5 rounded-lg text-xs ${tab === t.k ? "bg-[#d4a24c] text-black font-semibold" : "text-slate-300 hover:bg-white/5"}`}
                data-testid={`admin-comm-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>
        }
      />

      {tab === "history" && (
        <Card testid="admin-comm-history">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="py-3 pr-4">Quando</th>
                <th className="py-3 pr-4">Destinatário</th>
                <th className="py-3 pr-4">Template</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Cartão</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-500">Nenhum envio ainda</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5" data-testid={`admin-comm-row-${r.id}`}>
                  <td className="py-3 pr-4 text-slate-400 text-xs">{dt(r.created_at)}</td>
                  <td className="py-3 pr-4">
                    <div className="text-slate-100">{r.treatment_applied ? `${r.treatment_applied} ` : ""}{r.name_applied}</div>
                    <div className="text-[11px] text-slate-500">{r.to_email}</div>
                  </td>
                  <td className="py-3 pr-4"><span className="font-mono text-[#d4a24c] text-xs">{r.template_id || "WELCOME_V1"}</span></td>
                  <td className="py-3 pr-4">
                    <Pill tone={r.status === "sent" ? "green" : r.status === "failed" ? "red" : "amber"}>
                      {r.status}
                    </Pill>
                  </td>
                  <td className="py-3 pr-4">
                    {r.card_url && (
                      <a href={r.card_url} target="_blank" rel="noreferrer"
                        className="text-[#d4a24c] text-xs hover:underline">
                        Ver imagem →
                      </a>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    {r.user_id && !r.user_id.startsWith("__test__") && (
                      <button onClick={()=>reissue(r.user_id, r.name_applied)}
                        className="px-3 py-1 rounded-lg border border-white/10 text-xs text-slate-200 hover:bg-white/5 flex items-center gap-1"
                        data-testid={`admin-comm-reissue-${r.id}`}>
                        <Repeat className="w-3 h-3"/> Reenviar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === "preview" && (
        <Card title="Prévia do cartão personalizado" kicker="WELCOME_V1" testid="admin-comm-preview">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <select value={previewTreatment} onChange={e=>setPreviewTreatment(e.target.value)}
              className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-comm-preview-treatment">
              <option value="">— sem tratamento —</option>
              <option value="Dr.">Dr.</option>
              <option value="Dra.">Dra.</option>
            </select>
            <input placeholder="Nome do cliente" value={previewName} onChange={e=>setPreviewName(e.target.value)}
              className="md:col-span-2 bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-comm-preview-name"/>
            <button onClick={doPreview} disabled={previewing}
              className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="admin-comm-preview-btn">
              <Eye className="w-4 h-4"/> {previewing ? "Gerando…" : "Gerar prévia"}
            </button>
          </div>
          {previewSrc ? (
            <img src={previewSrc} alt="prévia" className="w-full rounded-xl border border-white/10" data-testid="admin-comm-preview-img"/>
          ) : (
            <div className="h-48 flex items-center justify-center text-sm text-slate-500 border border-dashed border-white/10 rounded-xl">
              Clique em "Gerar prévia" para visualizar o cartão personalizado.
            </div>
          )}
        </Card>
      )}

      {tab === "send-test" && (
        <Card title="Enviar teste para mim" kicker="Sem marcar cliente real" testid="admin-comm-send-test">
          <p className="text-sm text-slate-400 mb-4">
            Use este formulário para validar imagem, texto, tratamento, posicionamento e canal.
            Nenhum cliente real será marcado como tendo recebido a comunicação.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input placeholder="E-mail destino (padrão: seu)" value={testEmail} onChange={e=>setTestEmail(e.target.value)}
              className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-comm-test-email"/>
            <input placeholder="Nome (padrão: seu)" value={testName} onChange={e=>setTestName(e.target.value)}
              className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-comm-test-name"/>
            <select value={testTreatment} onChange={e=>setTestTreatment(e.target.value)}
              className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-comm-test-treatment">
              <option value="">— sem tratamento —</option>
              <option value="Dr.">Dr.</option>
              <option value="Dra.">Dra.</option>
            </select>
          </div>
          <button onClick={sendTest} disabled={sending}
            className="mt-4 px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
            data-testid="admin-comm-test-btn">
            <Send className="w-4 h-4"/> {sending ? "Enviando…" : "Enviar teste"}
          </button>
          {testResult && (
            <div className={`mt-4 p-3 rounded-lg text-sm border ${testResult.ok ? "bg-emerald-900/30 border-emerald-800 text-emerald-200" : "bg-rose-900/30 border-rose-800 text-rose-200"}`}
              data-testid="admin-comm-test-result">
              {testResult.ok
                ? <>✔ Enviado com sucesso. Status: <b>{testResult.record?.status}</b>. Message-ID: <code>{testResult.record?.message_id}</code></>
                : <>✕ Falha: {testResult.error || testResult.record?.error || "desconhecida"}</>}
            </div>
          )}
        </Card>
      )}

      <div className="mt-6">
        <Card kicker="Sobre" title="Como funciona o envio automático" testid="admin-comm-info">
          <ul className="text-sm text-slate-400 space-y-2 list-disc list-inside">
            <li>Todo novo cliente recebe automaticamente o cartão personalizado assim que faz o primeiro login.</li>
            <li>A imagem oficial é <code className="text-[#d4a24c]">/app/backend/assets/welcome_card.png</code> — mesma composição enviada por você.</li>
            <li>O nome do destinatário é sobreposto em dourado sobre a linha reservada, mantendo o Hermes, o título e a marca Del Padre intactos.</li>
            <li>Idempotência: o mesmo usuário não recebe duas vezes por renovação ou troca de plano.</li>
            <li>Reenvios manuais e testes ficam registrados aqui, no histórico e na auditoria (<code>WELCOME_TEST_SENT</code>, <code>WELCOME_REISSUED</code>).</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
